import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import BrandMark from "./BrandMark";
import { APP_SIGN_IN_URL, APP_SIGN_UP_URL } from "@/lib/appLinks";
import { scrollToId } from "@/lib/scroll";

const NAV = [
  { label: "How it works", id: "how-it-works", testid: "nav-how-it-works-link" },
  { label: "Pricing", id: "pricing", testid: "nav-pricing-link" },
];

const Header = () => {
  const [open, setOpen] = useState(false);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-guinep-line/50 bg-white/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-guinep-soft">
            <BrandMark size={17} />
          </span>
          <span className="font-serif text-lg font-semibold tracking-tight text-guinep-ink">Guinep Seed</span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={n.testid}
              onClick={() => go(n.id)}
              className="text-sm font-medium text-guinep-mute transition-colors duration-300 hover:text-guinep-ink"
            >
              {n.label}
            </button>
          ))}
          <a
            href={APP_SIGN_IN_URL}
            data-testid="nav-signin-button"
            className="text-sm font-medium text-guinep-mute transition-colors duration-300 hover:text-guinep-ink"
          >
            Sign in
          </a>
          <a
            href={APP_SIGN_UP_URL}
            data-testid="nav-signup-button"
            className="rounded-full bg-guinep-deep px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-guinep-mid hover:shadow-[0_8px_24px_-8px_rgba(31,58,43,0.5)]"
          >
            Get started
          </a>
        </nav>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-guinep-line text-guinep-ink md:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-guinep-line/50 bg-white md:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  data-testid={`mobile-${n.testid}`}
                  onClick={() => go(n.id)}
                  className="rounded-lg px-3 py-3 text-left text-base font-medium text-guinep-ink transition-colors hover:bg-guinep-soft"
                >
                  {n.label}
                </button>
              ))}
              <a
                href={APP_SIGN_IN_URL}
                data-testid="mobile-nav-signin-button"
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-3 text-base font-medium text-guinep-ink transition-colors hover:bg-guinep-soft"
              >
                Sign in
              </a>
              <a
                href={APP_SIGN_UP_URL}
                data-testid="mobile-nav-signup-button"
                onClick={() => setOpen(false)}
                className="mt-2 rounded-full bg-guinep-deep px-5 py-3 text-center text-base font-semibold text-white"
              >
                Get started
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
